import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { bmxurl } from 'src/globals';

//One line of the Daysheet's order: a resource or a resource group.
export interface daysheetorderitem {
  propertyid: string;
  //RESOURCE or RESOURCEGROUP.
  propertytype: string;
  name: string;
  showondaysheet: boolean;
  //For a resource group, the resourceIds of the resources in it, in the group's own order.
  members: string[];
  //For a resource, the resource itself as ResourceList gave it. The Daysheet's own list leaves
  //out resources switched off for it, so a shown group draws those members from here.
  resource?: any;
}

/*
  The order the Daysheet shows resources and resource groups in, and which it shows - set on the
  Daysheet Resource Order page and saved through ResourceGroupResourceOrder.

  Read in one place so the Daysheet and the page that sets the order always agree. Resources and
  groups come from their own lists and are merged into one order here.
*/
@Injectable({
  providedIn: 'root'
})
export class DaysheetOrderService {

  constructor(private http: HttpClient) { }

  /*
    Every resource and resource group, in Daysheet order. failed is true when either list could
    not be had, so a caller can fall back to what it did before rather than show half a sheet.

    The order comes from each one's orderindex. Until a Daysheet order has been saved those are
    all 0, so resources then come in their Resources screen order (displayorder), with the
    groups after them.
  */
  load(venueId: string): Observable<{ items: daysheetorderitem[], failed: boolean }> {
    return forkJoin({
      resources: this.http.get(bmxurl + "ResourceList/" + venueId + "//false").pipe(catchError(() => of(null))),
      groups: this.http.get(bmxurl + "ResourceGroupNameList/" + venueId + "/false").pipe(catchError(() => of(null)))
    }).pipe(
      switchMap(result => {
        const groups: any[] = (result.groups as any)?.data ?? [];

        //The group list may not say which resources are in each group; a group fetched on its
        //own does. Only the ones that need it are fetched.
        const lacking = groups.filter(group => !Array.isArray(group.resources));

        const fetched = lacking.length == 0
          ? of([] as any[])
          : forkJoin(lacking.map(group =>
              this.http.get(bmxurl + "resourcegroupname/" + venueId + "/" + group.resourcegroupnameId).pipe(
                map(response => (response as any)?.data ?? group),
                catchError(() => of(group)))));

        return fetched.pipe(map(full => {
          const byId = new Map<string, any>();
          (full as any[]).forEach(group => byId.set(String(group.resourcegroupnameId), group));

          return {
            resources: (result.resources as any)?.data ?? [],
            groups: groups.map(group => byId.get(String(group.resourcegroupnameId)) ?? group),
            failed: result.resources == null || result.groups == null
          };
        }));
      }),
      map(loaded => ({ items: this.merge(loaded.resources, loaded.groups), failed: loaded.failed }))
    );
  }

  private merge(resources: any[], groups: any[]): daysheetorderitem[] {
    const idOf = (res: any): string => (res?.resourceId ?? res?.resourceid ?? res?.id ?? "").toString();

    const merged = [
      ...resources.map((res: any, at: number) => ({
        item: {
          propertyid: idOf(res),
          propertytype: "RESOURCE",
          name: res.name ?? "",
          showondaysheet: res.showondaysheet != false,
          members: [],
          resource: res
        } as daysheetorderitem,
        startorder: Number(res.orderindex) || 0,
        tiebreak: Number(res.displayorder ?? at) || 0
      })),
      ...groups.map((group: any, at: number) => ({
        item: {
          propertyid: (group.resourcegroupnameId ?? "").toString(),
          propertytype: "RESOURCEGROUP",
          name: group.name ?? "",
          showondaysheet: group.showondaysheet != false,
          members: (Array.isArray(group.resources) ? group.resources : []).map(idOf).filter((id: string) => id != "")
        } as daysheetorderitem,
        startorder: Number(group.orderindex) || 0,
        tiebreak: 100000 + at
      }))
    ];

    return merged
      .sort((one, two) => (one.startorder - two.startorder) || (one.tiebreak - two.tiebreak) || one.item.name.localeCompare(two.item.name))
      .map(entry => entry.item);
  }
}
